import React, { useMemo } from "react";
import { Progress } from "semantic-ui-react";
import styles from "./index.module.css";
import classNames from "classnames";
import { PickProgressData } from "../../OrderOverview/OrderOverview";
import RemainingQuantity from "./RemainingQuantity";
import ProgressButton from "./ProgressButton";

type Props = {
  isLoading?: boolean;
  disabled?: boolean;
  showRemainingPicks?: boolean;
  data: Omit<PickProgressData, "itemId">;
  totalQuantity: number;
  currentQuantity: number;
  onSideAction?: () => void;
};

const ProgressBar = ({
  isLoading = false,
  disabled = false,
  showRemainingPicks = true,
  totalQuantity,
  currentQuantity,
  data,
  onSideAction,
}: Props) => {
  const {
    pickedQuantity,
    adjustedPickedQuantity,
    confirmedQuantity,
    remainingQuantity,
    verifiedQuantity,
  } = data;
  const [isPickIncreased, isPickDecreased] = [
    adjustedPickedQuantity > pickedQuantity,
    adjustedPickedQuantity < pickedQuantity,
  ];

  const isNoPicks = adjustedPickedQuantity === 0 && confirmedQuantity > 0;

  const color = useMemo(() => {
    if (pickedQuantity < 1 && remainingQuantity > 0) return "green";
    if (isNoPicks) return "red";
    if (isPickDecreased) return "yellow";
    if (verifiedQuantity < pickedQuantity) return "blue";
    if (!isNoPicks) return "green";
  }, [isPickDecreased, isNoPicks, verifiedQuantity, pickedQuantity, remainingQuantity]);

  const progressBarClasses = classNames(
    styles["pick-progress--inner-width"],
    styles["progress-bar"],
    {
      [styles["pick-progress--exceeded-confirmed-quantity"]]: pickedQuantity > confirmedQuantity,
      [styles["pick-progress--loading"]]: isLoading,
      [styles["pick-progress--disabled"]]: disabled,
    }
  );

  return (
    <div className={styles["pick-progress"]}>
      <Progress
        className={progressBarClasses}
        size="medium"
        value={currentQuantity}
        total={totalQuantity}
        progress="ratio"
        color={color}
        active={adjustedPickedQuantity < confirmedQuantity}
        disabled={isLoading}
      ></Progress>
      {verifiedQuantity === pickedQuantity && showRemainingPicks && remainingQuantity > 0 && (
        <RemainingQuantity
          value={
            isPickIncreased
              ? remainingQuantity - (adjustedPickedQuantity - pickedQuantity)
              : remainingQuantity
          }
        />
      )}
      {onSideAction && verifiedQuantity < pickedQuantity && (
        <ProgressButton onClick={onSideAction} />
      )}
    </div>
  );
};

export default ProgressBar;
